'use client';

import { DocumentData } from 'firebase/firestore';
import { createContext, useCallback, useContext, useEffect, useMemo } from 'react';

import { useStepNavigator } from '~/ui';

import { NAVIGATOR_STEPS } from './steps';
import { NavigatorStep } from './types';
import { DEFAULT_STATE, useStateManager, UseStateManagerReturn } from './useStateManager';

type ContractState = UseStateManagerReturn['state'];

type StateManagerActions = Omit<UseStateManagerReturn, 'state'>;

type StepNavigatorActions = Omit<
  ReturnType<typeof useStepNavigator<NavigatorStep>>,
  'getRedirectTo' | 'getHistory' | 'navigateWithRedirect' | 'setRedirectTo'
>;

type CreateContractContext = StateManagerActions &
  StepNavigatorActions & {
    contractData: ContractState['contractData'];
    getContractData: () => ContractState['contractData'];
    onCompletion: () => void;
  };

const CreateContractContext = createContext<CreateContractContext>({
  contractData: DEFAULT_STATE.contractData,
  getContractData: () => DEFAULT_STATE.contractData,
  getCurrentStep: () => NAVIGATOR_STEPS[0],
  goToNextStep: () => {},
  goToPreviousStep: () => {},
  onCompletion: () => {},
  setContractNumber: () => {},
  setCurrentStep: () => {},
  setSignedContract: () => {},
});

interface CreateContractProviderProps {
  children: React.ReactNode;
  onCompletion: () => void;
  onUpdate: (data: ContractState['contractData']) => void;
  onboardingData?: DocumentData | null;
}

export const CreateContractProvider = ({
  children,
  onCompletion,
  onUpdate,
  onboardingData,
}: CreateContractProviderProps) => {
  const { state, setContractNumber, setSignedContract } = useStateManager({ onboardingData });

  const { getCurrentStep, goToNextStep, goToPreviousStep, setCurrentStep } = useStepNavigator({
    initialStep: NAVIGATOR_STEPS[0],
    steps: NAVIGATOR_STEPS,
  });

  const getContractData = useCallback((): ContractState['contractData'] => {
    return state.contractData;
  }, [state.contractData]);

  useEffect(() => {
    onUpdate(state.contractData);
  }, [onUpdate, state.contractData]);

  const context = useMemo(
    () => ({
      getContractData,
      getCurrentStep,
      goToNextStep,
      goToPreviousStep,
      onCompletion,
      setContractNumber,
      setCurrentStep,
      setSignedContract,
      ...state,
    }),
    [
      getContractData,
      getCurrentStep,
      goToNextStep,
      goToPreviousStep,
      onCompletion,
      setContractNumber,
      setCurrentStep,
      setSignedContract,
      state,
    ]
  );

  return <CreateContractContext.Provider value={context}>{children}</CreateContractContext.Provider>;
};

export const useCreateContract = () => {
  const context = useContext(CreateContractContext);

  if (!context) {
    throw new Error('useCreateContract must be used within a CreateContractProvider');
  }

  return context;
};
